import Bugsnag from "@bugsnag/js";
import { t } from "@lingui/macro";
import { QuizResponseType, WahooTokenType } from "@WahooFitness/cloud-client-types";
import { useConfigContext, useSnackbarContext } from "@WahooFitness/wahoo-offline-mfe";
import { useCallback } from "react";
import type { SanitizedAnswers } from "./useQuiz";

type QuizClient<TResults> = {
  post: (token: WahooTokenType, response: QuizResponseType) => Promise<TResults>;
};

function useQuizOperations<TResults>(client: QuizClient<TResults>) {
  const { wahooToken, locale } = useConfigContext();
  const { enqueueSnackbar } = useSnackbarContext();

  const getQuizResults = useCallback(
    async (quizResponse: QuizResponseType): Promise<{ results?: TResults; error?: string }> => {
      try {
        const results = await client.post(wahooToken, quizResponse);

        return { results };
      } catch (err) {
        const errorToNotify = err instanceof Error ? err : new Error(String(err));
        Bugsnag.notify(errorToNotify, function (event) {
          event.severity = "error";
        });
        enqueueSnackbar({
          message: t`There was a problem submitting your questionnaire. Check your network and try again.`,
          severity: "error",
        });
        return { error: "error submitting quiz" };
      }
    },
    [client, enqueueSnackbar, wahooToken]
  );

  const buildResponse = useCallback(
    (
      answers: SanitizedAnswers,
      questionnaireId: string,
      userId: number | undefined
    ): QuizResponseType => ({
      questionnaire_id: questionnaireId,
      answers,
      language_code: locale,
      user_id: userId,
    }),
    [locale]
  );

  return { getQuizResults, buildResponse };
}

export default useQuizOperations;
